import {
	FLAG_ENABLE_TIMERS,
	PIGGY_BANK_MAX_PROGRESS,
	PIGGY_BANK_PROGRESS_PER_CLICK,
	PIGGY_BANK_QUEST_ID,
} from "@/config/constants";
import { ResourceType } from "@/game/entities/config/resources";
import { DURABILITY_EXPLORED } from "@/game/MainMapScene";
import QuestRepository from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import { getSettlementCastleFrameNameByLevel } from "@/game/utils/settlement";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import { EmitterEvents } from "@/hooks/useEmitter";
import useSegmentManager from "@/hooks/useSegmentManager";
import { CalculationType, OfferProvider } from "@/services/OfferProvider";
import useEventStore from "@/store/useEventStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { SuperClickerEnum } from "@/store/useRuntimeStore";
import useSettlementsStore, {
	type SettlementKey,
} from "@/store/useSettlementsStore";
import useShopStore from "@/store/useShopStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import {
	type ProductionValue,
	type TileConfigItem,
	TileEntityParam,
	type TileIndex,
	type TileNeighbour,
	type UpgradeObjective,
	UpgradeObjectiveAction,
	type UpgradeObjectiveFrameNameData,
	type UpgradeObjectiveNumberData,
	type UpgradeObjectiveResourceData,
	UpgradeObjectiveType,
	type UpgradeParams,
} from "@/types/tile";
import { EventCategory } from "@/types/ui";
import { formatNumber, formatPercent } from "@/utils/format";
import { ceilNumber, floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import {
	FrameName,
	MAIN_SETTLEMENT_TILE_INDEX,
	tilerepoSeasFrameNames,
} from "../config";
import { QuestObjectiveActions } from "../config/quests/quest_objective_actions";
import {
	getSettlementConfigByLevel,
	MAX_SETTLEMENT_LEVEL,
} from "../config/settlements";
import { TileVisibilityType } from "../config/terrain";
import { EventUpgradeIds, UpgradeIds } from "../config/upgrades";
import TileEntity, {
	type TileEntityWithOnActivateMethod,
	type TileEntityWithOnClickMethod,
	type TileEntityWithOnRevealMethod,
	type TileEntityWithOnTickMethod,
} from "../TileEntity";

const COINS_INCREMENT_PER_TICK = 10;

const CACHEABLE_UPGRADE_OBJECTIVE_ACTIONS = new Set<UpgradeObjectiveAction>([
	UpgradeObjectiveAction.BUILDING_COUNT,
	UpgradeObjectiveAction.EXPLORED_COUNT,
	UpgradeObjectiveAction.INCOME,
	// UpgradeObjectiveAction.ROAD_PERCENTAGE,
	UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT,
	UpgradeObjectiveAction.SETTLEMENT_COUNT,
	UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
	UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT,
	// UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
	UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE,
	// UpgradeObjectiveAction.SETTLEMENT_LEVEL_ACCUMULATED,
	// UpgradeObjectiveAction.SETTLEMENT_INCOME,
	UpgradeObjectiveAction.BUILDING_INCOME,
	UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT,
]);

type SettlementCastleFrameNames =
	| FrameName.SETTLEMENT_CASTLE
	| FrameName.SETTLEMENT_CASTLE_LEVEL1
	| FrameName.SETTLEMENT_CASTLE_LEVEL2
	| FrameName.SETTLEMENT_CASTLE_LEVEL3
	| FrameName.SETTLEMENT_CASTLE_LEVEL4
	| FrameName.SETTLEMENT_CASTLE_LEVEL5
	| FrameName.SETTLEMENT_CASTLE_LEVEL6
	| FrameName.SETTLEMENT_CASTLE_LEVEL7
	| FrameName.SETTLEMENT_CASTLE_LEVEL8
	| FrameName.SETTLEMENT_CASTLE_LEVEL9
	| FrameName.SETTLEMENT_CASTLE_LEVEL10;

class SettlementCastleBuildingTileEntity
	extends TileEntity
	implements
		TileEntityWithOnClickMethod,
		TileEntityWithOnActivateMethod,
		TileEntityWithOnRevealMethod,
		TileEntityWithOnTickMethod
{
	public static readonly frameName: SettlementCastleFrameNames =
		FrameName.SETTLEMENT_CASTLE;

	private readonly settlementsStore = useSettlementsStore();

	private readonly resourcesStore = useResourcesStore();

	private questCurrentValueCache: number | null = null;

	private _level: number | null = null;

	private cachedUpgradesLevel: number | null = null;

	protected get config(): TileConfigItem {
		const config = super.config;
		const configByLevel = getSettlementConfigByLevel(this.level);

		return {
			...config,
			...configByLevel,
			production: new Map<ResourceType, ProductionValue>([
				...config.production,
				...configByLevel.production,
			]),
		};
	}

	// doplnime hradu tuto propsu, lebo pri prieskume sa pracuje s kliknutou entitou
	public get exploreCoefficient(): number {
		return 0;
	}

	public get upgrades(): Map<FrameName, UpgradeParams> {
		if (this.cachedUpgrades && this.cachedUpgradesLevel === this.level)
			return this.cachedUpgrades;

		const upgrades = new Map<FrameName, UpgradeParams>(
			// Nepouzivame super.upgrades, lebo tam je logika pre beznu entitu, ktora sa cachuje.
			this.config.upgrades,
		);

		// Apply upgrade 27
		let castleUpgrades = upgrades.get(FrameName.SETTLEMENT_CASTLE);
		if (!castleUpgrades?.price) {
			this.cachedUpgradesLevel = this.level;
			return (this.cachedUpgrades = upgrades);
		}

		const updatedPrice = this.resolveDynamicResources(castleUpgrades.price);
		let coinPrice = updatedPrice[ResourceType.COINS] || 0;
		coinPrice *= 1 - getUpgradeCurrentReplaceValueById(UpgradeIds.haggling);
		coinPrice = floorNumber(coinPrice);

		upgrades.set(FrameName.SETTLEMENT_CASTLE, {
			...castleUpgrades,
			price: { ...castleUpgrades.price, [ResourceType.COINS]: coinPrice },
		});

		castleUpgrades = upgrades.get(FrameName.SETTLEMENT_CASTLE);
		if (!castleUpgrades?.price) {
			this.cachedUpgradesLevel = this.level;
			return (this.cachedUpgrades = upgrades);
		}
		// Apply upgrade 37
		const updatedPrices: Partial<Record<ResourceType, number>> =
			Object.fromEntries(
				(
					Object.entries(castleUpgrades.price) as Array<
						[ResourceType, number]
					>
				).map(
					([resource, amount]: [ResourceType, number]): [
						string,
						number,
					] => [
						resource,
						amount *
							(resource !== ResourceType.COINS
								? 1 -
									getUpgradeCurrentReplaceValueById(
										UpgradeIds.architecture,
									)
								: 1),
					],
				),
			);

		upgrades.set(FrameName.SETTLEMENT_CASTLE, {
			...castleUpgrades,
			price: updatedPrices,
		});

		this.cachedUpgradesLevel = this.level;
		return (this.cachedUpgrades = upgrades);
	}

	public set level(value: number | null) {
		this._level = value;
	}

	public get level(): number {
		if (this._level !== null) {
			return this._level;
		}
		return (
			this.settlementsStore.getSettlementByCoordinates({
				x: this.x,
				y: this.y,
			})?.level ?? 1
		);
	}

	public get frameName(): FrameName {
		return getSettlementCastleFrameNameByLevel(this.level);
	}

	public get distance(): number {
		const mapCenterPoint = this.tilerepo.MAP_SIZE / 2;
		const distX = this.x - mapCenterPoint;
		const distY = this.y - mapCenterPoint;
		return Math.abs(distX) + Math.abs(distY);
	}

	public get name(): string {
		return this.settlementName;
	}

	public get settlementName(): string {
		return (
			this.settlementsStore.getSettlementByCoordinates({
				x: this.x,
				y: this.y,
			})?.name ??
			super.name ??
			""
		);
	}

	public get availableClicks(): number {
		const settlement = this.settlementsStore.getSettlementByCoordinates({
			x: this.x,
			y: this.y,
		});
		return settlement!.availableClicks ?? 0;
	}

	public get maxClicks(): number {
		let maxClicks =
			this.config.storage.get(ResourceType.SETTLEMENT_CLICKS) || 0;
		maxClicks *= 1 + getUpgradeCurrentReplaceValueById(UpgradeIds.reeve);
		maxClicks = ceilNumber(maxClicks);

		return maxClicks;
	}

	public get progress(): number {
		return +(this.availableClicks / this.maxClicks).toFixed(4);
	}

	// TODO: check store value
	public get isRevealed(): boolean {
		if (this.index === MAIN_SETTLEMENT_TILE_INDEX) return true;

		return (
			this.tilerepo.getParam(this.index, TileEntityParam.VISIBILITY) ===
			TileVisibilityType.EXPLORED
		);
	}

	// TODO: check store value
	public get isActive(): boolean {
		if (this.index === MAIN_SETTLEMENT_TILE_INDEX) return true;

		return this.isRevealed && this.isConnectedToMainSettlement;
	}

	public get isMainSettlement(): boolean {
		return this.index === MAIN_SETTLEMENT_TILE_INDEX;
	}

	public get activeQuestRange(): number | null {
		if (!this.activeQuest?.objective) return null;

		const { action, data } = this.activeQuestObjective || {};
		if (
			!action ||
			![
				UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE,
				UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE,
			].includes(action)
		)
			return null;

		if (this.isQuestCompleted) return null;

		const range = (data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
		return range;
	}

	private get activeQuest(): UpgradeParams | null {
		if (!this.isActive) return null;
		if (!this.upgrades.size) return null;

		const entries = this.upgrades.entries();
		if (!entries) return null;

		const [[, quest]] = entries;
		if (!quest) return null;

		return quest;
	}

	public get activeQuestObjective(): UpgradeObjective | null {
		if (!this.activeQuest?.objective) return null;

		return this.isMainSettlement
			? this.activeQuest.objective[UpgradeObjectiveType.MAIN]
			: (this.activeQuest.objective?.[UpgradeObjectiveType.SIDE] ??
					this.activeQuest.objective[UpgradeObjectiveType.MAIN]);
	}

	public get questProgressValues(): {
		currentValue: number;
		targetValue: number;
	} | null {
		if (!this.isActive) return null;
		if (!this.activeQuest?.objective) return null;

		const { action, data } = this.activeQuestObjective || {};
		if (!action || !data) return null;

		let currentValue = 0;
		let targetValue = 0;

		switch (action) {
			case UpgradeObjectiveAction.SETTLEMENT_CLICK_COUNT: {
				currentValue =
					this.questCurrentValueCache ??
					this.settlementsStore.getSettlementByCoordinates({
						x: this.x,
						y: this.y,
					})?.totalClicks ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.EXPLORED_COUNT: {
				const terrains = (data as UpgradeObjectiveFrameNameData)[
					"{terrains}"
				];
				if (terrains?.size) {
					currentValue =
						this.questCurrentValueCache ??
						this.tilerepo.getTileCount0(terrains) ??
						0;
				} else {
					currentValue =
						this.questCurrentValueCache ??
						this.tilerepo.exploredTileCount ??
						0;
				}
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_COUNT: {
				currentValue =
					this.questCurrentValueCache ??
					this.tilerepo.activeSettlementCount ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{2to4}"] ??
					(data as UpgradeObjectiveNumberData)["{number}"] ??
					0;
				break;
			}
			case UpgradeObjectiveAction.INCOME: {
				const resource = (data as UpgradeObjectiveResourceData)[
					"{resource}"
				];

				if (resource === ResourceType.FISH_INCOME) {
					currentValue =
						this.questCurrentValueCache ??
						this.tilerepo.getFishIncome();
				} else {
					currentValue =
						this.questCurrentValueCache ??
						this.resourcesStore[resource] ??
						0;
				}
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_BUILDING_COUNT: {
				const buildings = (data as UpgradeObjectiveFrameNameData)[
					"{buildings}"
				];
				currentValue =
					this.questCurrentValueCache ??
					this.tilerepo.getSettlementTileCount2(
						{ x: this.x, y: this.y },
						buildings,
					) ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{one}"] ??
					(data as UpgradeObjectiveNumberData)["{2to4}"] ??
					(data as UpgradeObjectiveNumberData)["{number}"] ??
					0;
				break;
			}
			// case UpgradeObjectiveAction.ROAD_PERCENTAGE: {
			// 	const buildings = (data as UpgradeObjectiveFrameNameData)[
			// 		"{buildings}"
			// 	];
			// 	currentValue =
			// 		this.questCurrentValueCache ??
			// 		(this.tilerepo.getTileCount2(buildings) ?? 0) /
			// 			(this.tilerepo.getTileCount2(tilerepoRoadsFrameNames) ??
			// 				0);
			// 	if (isNaN(currentValue) || currentValue === Infinity)
			// 		currentValue = 0;
			// 	targetValue =
			// 		(data as UpgradeObjectiveNumberData)["{percent}"] ?? 0;
			// 	break;
			// }
			case UpgradeObjectiveAction.BUILDING_COUNT: {
				const buildings = (data as UpgradeObjectiveFrameNameData)[
					"{buildings}"
				];
				currentValue =
					this.questCurrentValueCache ??
					this.tilerepo.getTileCount2(buildings) ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_EXPLORED_RANGE: {
				const range =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				currentValue =
					this.questCurrentValueCache ??
					this.tilerepo.getNeighbourTileIndexesByTileCoordinates(
						{
							x: this.x,
							y: this.y,
						},
						range,
						true,
						new Set<TileVisibilityType>([
							TileVisibilityType.EXPLORED,
						]),
						true,
					).length ??
					0;
				targetValue = Math.pow(range * 2 + 1, 2);
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_LEVEL_ACCUMULATED: {
				currentValue =
					this.questCurrentValueCache ??
					Array.from(useSettlementsStore().settlementKeys).reduce(
						(acc: number, key: SettlementKey): number => {
							// eslint-disable-next-line no-restricted-syntax
							const [x, y] = key.split("|").map(Number);
							const entity =
								this.tilerepo.createEntityByFrameName(
									FrameName.SETTLEMENT_CASTLE,
									{ x, y },
								) as SettlementCastleBuildingTileEntity | null;
							if (!entity || !entity.isActive) return acc;
							return acc + entity.level;
						},
						0,
					) ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.UNLOCKED_UPGRADE_COUNT: {
				currentValue =
					this.questCurrentValueCache ??
					useUpgradesStore().ownedUpgradeCount ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_ON_LEVEL_COUNT: {
				const level =
					(data as UpgradeObjectiveNumberData)["{level}"] ?? 0;
				currentValue =
					this.questCurrentValueCache ??
					this.tilerepo.getActiveSettlementCountOnLevelOrHigher(
						level,
					) ??
					0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED: {
				const settlement =
					this.settlementsStore.getSettlementByCoordinates({
						x: this.x,
						y: this.y,
					});
				currentValue =
					// Nepotrebujeme cache, lebo to je cachnute v storku.
					// this.questCurrentValueCache ??
					settlement?.questData?.current ?? 0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			case UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE: {
				const settlement =
					this.settlementsStore.getSettlementByCoordinates({
						x: this.x,
						y: this.y,
					});

				// osetrenie na konta, ktore uz su na danom leveli v dobe releasu
				// nula alebo undefined - nulu mozu mat uz ulozenu v storku z level-up akcie, co je legit stav, tak vtedy nechceme znova zistovat stav
				if (
					!settlement?.questData?.target &&
					settlement?.questData?.target !== 0
				) {
					// Tu nemozme pouzit this.range, lebo to early exitne skorej, ako mame setnute questove data.
					const range =
						this.activeQuestObjective?.data?.["{number}"] ?? 0;
					const target = this.tilerepo.getBlockedSettlementTileCount2(
						{ x: this.x, y: this.y },
						range,
					);
					this.settlementsStore.setSettlementPropsByCoordinates(
						{ x: this.x, y: this.y },
						{
							questData: {
								target,
							},
						},
					);
				}

				const range =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				targetValue = settlement?.questData?.target ?? 0;
				currentValue =
					this.questCurrentValueCache ??
					targetValue -
						this.tilerepo.getBlockedSettlementTileCount2(
							{ x: this.x, y: this.y },
							range,
						);
				break;
			}
			/*
			case UpgradeObjectiveAction.SETTLEMENT_INCOME: {
				const settlement =
					this.settlementsStore.getSettlementByCoordinates({
						x: this.x,
						y: this.y,
					});
				currentValue =
					// Nepotrebujeme cache, lebo to je cachnute v storku.
					// this.questCurrentValueCache ??
					settlement?.questData?.current ?? 0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			*/
			case UpgradeObjectiveAction.BUILDING_INCOME: {
				const buildings = (data as UpgradeObjectiveFrameNameData)[
					"{buildings}"
				];
				const resource = (data as UpgradeObjectiveResourceData)[
					"{resource}"
				];
				const income = this.tilerepo
					.getAllTileEntitesByFrameNames(buildings)
					.reduce((acc: number, entity: TileEntity): number => {
						if (!entity.production.has(resource)) return acc;
						return (
							acc + (entity.production.get(resource)! as number)
						);
					}, 0);

				currentValue = this.questCurrentValueCache ?? income ?? 0;
				targetValue =
					(data as UpgradeObjectiveNumberData)["{number}"] ?? 0;
				break;
			}
			default:
				return null;
		}

		return { currentValue, targetValue };
	}

	public get questDescriptionHtml(): string {
		if (!this.isActive) return "";
		if (!this.activeQuest?.objective) return "";

		const { text, data } = this.activeQuestObjective || {};
		if (!text) return "";
		if (!data) return text;

		let description: string = translate(text);

		Object.entries(data).forEach(
			([key, value]: [
				string,
				number | Set<FrameName> | ResourceType,
			]): void => {
				if (typeof value === "number") {
					description = description.replace(
						key,
						(key === "{percent}"
							? formatPercent(value)
							: formatNumber(value)
						).toString(),
					);
				}

				if (value instanceof Set) {
					if (value.size === 1) {
						if (key === "{terrains}") key = "{terrain}";
						if (key === "{buildings}") key = "{building}";
						description = description.replace(
							key,
							translate(
								`${
									value.values().next().value as FrameName
								}.name`,
							),
						);
					}
				}
			},
		);

		return description;
	}

	public get questHint(): string {
		if (!this.isActive) return "";
		if (!this.activeQuest?.objective) return "";

		const { hint, data } = this.activeQuestObjective || {};
		if (!hint) return "";
		if (!data) return hint;

		let questHint: string = translate(hint);

		Object.entries(data).forEach(
			([key, value]: [
				string,
				number | Set<FrameName> | ResourceType,
			]): void => {
				if (typeof value === "number") {
					questHint = questHint.replace(
						key,
						(key === "{percent}"
							? formatPercent(value)
							: formatNumber(value)
						).toString(),
					);
				}
			},
		);

		return questHint;
	}

	public get questProgressHtml(): string {
		const { currentValue, targetValue } = this.questProgressValues ?? {};
		// undefined lebo moze nadobudat hodnotu nula
		if (currentValue === undefined || targetValue === undefined) return "";

		// const isPercentValue =
		// 	this.activeQuest?.objective?.action ===
		// 	UpgradeObjectiveAction.ROAD_PERCENTAGE;
		const isPercentValue = false;
		const formater = (value: number): string =>
			(isPercentValue
				? formatPercent(value, 1)
				: formatNumber(value)
			).toString();

		return `${formater(currentValue)} / <strong>${formater(targetValue)}</strong>`;
	}

	public get isQuestCompleted(): boolean {
		if (!this.isActive) return false;

		const { isQuestSkipEnabled } =
			this.settlementsStore.getSettlementByCoordinates({
				x: this.x,
				y: this.y,
			})!;
		if (isQuestSkipEnabled) return true;

		const { currentValue, targetValue } = this.questProgressValues ?? {};
		// undefined lebo moze nadobudat hodnotu nula
		if (currentValue === undefined || targetValue === undefined)
			return false;

		return currentValue >= targetValue;
	}

	public get canLevelUp(): boolean {
		if (!this.isActive) return false;
		if (
			!this.isMainSettlement &&
			this.level >= this.settlementsStore.mainLevel
		)
			return false;
		if (this.level >= MAX_SETTLEMENT_LEVEL) return false;
		if (
			!this.isMainSettlement &&
			this.level >= this.settlementsStore.mainLevel
		)
			return false;

		const { consumption } = getSettlementConfigByLevel(this.level + 1);
		if (!consumption) return false;

		if (
			consumption.has(ResourceType.FOOD_CONSUMPTION) &&
			!this.resourcesStore.validateResourceAmount(
				ResourceType.FOOD_DIFF,
				consumption.get(ResourceType.FOOD_CONSUMPTION)! -
					(this.consumption.has(ResourceType.FOOD_CONSUMPTION)
						? this.consumption.get(ResourceType.FOOD_CONSUMPTION)!
						: 0),
			)
		)
			return false;

		const { price } = this.upgrades.get(FrameName.SETTLEMENT_CASTLE)!;
		if (!price) return false;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			return entries.every(
				([key, value]: [ResourceType, number]): boolean =>
					this.resourcesStore.validateResourceAmount(key, value),
			);
		}

		return true;
	}

	public levelUp(): void {
		if (!this.isActive) return;
		if (!this.canLevelUp) return;

		const { price } = this.upgrades.get(FrameName.SETTLEMENT_CASTLE)!;
		if (!price) return;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			entries.forEach(([key, value]: [ResourceType, number]): void => {
				this.resourcesStore.decrease(
					key,
					value,
					"settlement: level up",
				);
			});
		}

		this.cachedUpgrades = null;
		this.questCurrentValueCache = null;
		if (
			useEventStore().attributes.type === EventCategory.FIREFEST &&
			getUpgradeCurrentReplaceValueById(
				EventUpgradeIds.ff_town_celebrations,
			) > 0
		) {
			const newLevel = this.level + 1;
			let townCelebrationsReward = 0;

			if (newLevel <= 10) {
				townCelebrationsReward = 500;
			} else if (newLevel <= 20) {
				townCelebrationsReward = 1_000;
			} else if (newLevel <= 30) {
				townCelebrationsReward = 2_000;
			} else if (newLevel <= 40) {
				townCelebrationsReward = 4_000;
			} else if (newLevel <= 50) {
				townCelebrationsReward = 8_000;
			} else if (newLevel <= 60) {
				townCelebrationsReward = 15_000;
			} else if (newLevel <= 70) {
				townCelebrationsReward = 30_000;
			} else {
				townCelebrationsReward = 60_000;
			}

			this.resourcesStore.increase(
				ResourceType.EVENTIUM,
				townCelebrationsReward,
				"Eventium income from upgrade: town_celebrations",
			);

			this.emitter.emit(EmitterEvents.PhaserEventiumIncome, {
				x: this.x,
				y: this.y,
				reward: { [ResourceType.EVENTIUM]: townCelebrationsReward },
			});
		}

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ level: this.level + 1, isQuestSkipEnabled: false, questData: {} },
		);

		// if next quest is...
		if (
			this.activeQuestObjective?.action ===
			UpgradeObjectiveAction.SETTLEMENT_CLEARED_RANGE
		) {
			// Tu nemozme pouzit this.range, lebo to early exitne skorej, ako mame setnute questove data.
			const range = this.activeQuestObjective.data?.["{number}"] ?? 0;
			const target = this.tilerepo.getBlockedSettlementTileCount2(
				{ x: this.x, y: this.y },
				range,
			);
			this.settlementsStore.setSettlementPropsByCoordinates(
				{ x: this.x, y: this.y },
				{
					questData: {
						target,
					},
				},
			);
		}

		const sm = useSegmentManager();
		sm.markSegmentAsDirtyByCoordinates({ x: this.x, y: this.y });
		sm.run(true);
	}

	public get canSkipQuest(): boolean {
		if (!this.isActive) return false;
		if (!this.activeQuest?.skipPrice) return false;

		const { skipPrice: price } = this.upgrades.get(
			FrameName.SETTLEMENT_CASTLE,
		)!;
		if (!price) return false;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			return entries.every(
				([key, value]: [ResourceType, number]): boolean =>
					this.resourcesStore.validateResourceAmount(key, value),
			);
		}

		return true;
	}

	public skipQuest(): void {
		if (!this.isActive) return;
		if (!this.canSkipQuest) return;

		const { skipPrice: price } = this.upgrades.get(
			FrameName.SETTLEMENT_CASTLE,
		)!;
		if (!price) return;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			entries.forEach(([key, value]: [ResourceType, number]): void => {
				this.resourcesStore.decrease(
					key,
					value,
					"settlement: skip quest",
				);
			});
		}

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ isQuestSkipEnabled: true },
		);
	}

	public get skipQuestGemsPrice(): number {
		if (!this.isActive) return 0;
		if (!this.activeQuest?.skipPrice) return 0;
		if (
			!this.isMainSettlement &&
			this.level >= this.settlementsStore.mainLevel
		)
			return 0;

		const { skipPrice: price } = this.upgrades.get(
			FrameName.SETTLEMENT_CASTLE,
		)!;
		if (!price) return 0;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			const gemsPrice = entries.find(
				([key]: [ResourceType, number]): boolean =>
					key === ResourceType.GEMS,
			);

			return gemsPrice ? gemsPrice[1] : 0;
		}

		return 0;
	}

	public initSettlementQuestCache(): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("initSettlementQuestCache");
		}
		if (
			CACHEABLE_UPGRADE_OBJECTIVE_ACTIONS.has(
				// @ts-expect-error - TS2345
				this.activeQuest?.objective?.[
					this.isMainSettlement ? "main" : "side"
				]?.action,
			)
		) {
			if (this.questCurrentValueCache === null) {
				const { currentValue } = this.questProgressValues ?? {};
				if (currentValue !== undefined) {
					this.questCurrentValueCache = currentValue;
				}
			}
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("initSettlementQuestCache");
		}
	}

	public onClick(): void {
		this.fixIsRevealedStatus();
		this.fixIsActiveStatus();

		this.emitter.emit(EmitterEvents.PhaserSettlementCastleClick, {
			x: this.x,
			y: this.y,
		});

		if (!this.isConnectedToMainSettlement) {
			const neighbours = this.tilerepo
				.getNeighbourTileIndexesByTileCoordinates(
					{ x: this.x, y: this.y },
					0,
					false,
				)
				.filter(
					(tileIndex: TileIndex): boolean =>
						this.tilerepo.getParam(
							tileIndex,
							TileEntityParam.VISIBILITY,
						) === TileVisibilityType.EXPLORED,
				);

			if (neighbours.length > 0) {
				neighbours.forEach((tileIndex: TileIndex): void => {
					const entity = this.tilerepo.getTileEntityByTileIndex(
						tileIndex,
					) as TileEntity &
						TileEntityWithOnActivateMethod &
						TileEntityWithOnRevealMethod;
					if (!entity) return;

					const castleNeighbours = this.tilerepo
						.getNeighbourTileIndexesByTileCoordinates(
							{ x: entity.x, y: entity.y },
							0,
							false,
						)
						.filter(
							(tileIndex: TileIndex): boolean =>
								this.tilerepo.getParam(
									tileIndex,
									TileEntityParam.VISIBILITY,
								) === TileVisibilityType.EXPLORED,
						);

					if (castleNeighbours.length > 0) {
						castleNeighbours.forEach(
							(neighborTileIndex: TileIndex): void => {
								const neighborEntity =
									this.tilerepo.getTileEntityByTileIndex(
										neighborTileIndex,
									) as TileEntity &
										TileEntityWithOnActivateMethod &
										TileEntityWithOnRevealMethod;
								if (!neighborEntity) return;
								if (
									tilerepoSeasFrameNames.has(
										neighborEntity.frameName,
									)
								) {
									this.emitter.emit(
										EmitterEvents.PhaserWarningMessage,
										{
											x: this.x,
											y: this.y,
											text: "ui.connect_road_shipyard_town",
										},
									);
									if (!this.isActive) return;
								}
							},
						);
					}
				});
			}

			this.emitter.emit(EmitterEvents.PhaserWarningMessage, {
				x: this.x,
				y: this.y,
				text: "ui.connect_road_town",
			});
		}
		if (!this.isActive) return;

		if (!this.config.onClick) return;

		let numberOfClicks = 1;
		numberOfClicks += getUpgradeCurrentReplaceValueById(
			UpgradeIds.tax_collector,
		);

		let superClickerMultiplier = 1;
		if (useRuntimeStore().superClickerActive) {
			useRuntimeStore().setShowUseSuperClickerTooltip(false);
			const userStore = useUserStore();
			userStore.setSuperClickersUsed(userStore.superClickersUsed + 1);
			superClickerMultiplier = useRuntimeStore().superClickerType;
			if (useRuntimeStore().superClickerType === SuperClickerEnum.TEN) {
				superClickerMultiplier += getUpgradeCurrentReplaceValueById(
					UpgradeIds.butterfingers,
				);
			}
			numberOfClicks *= superClickerMultiplier;
		} else if (useResourcesStore().superClicker) {
			useRuntimeStore().setShowUseSuperClickerTooltip(true);
		}

		const settlement = this.settlementsStore.getSettlementByCoordinates({
			x: this.x,
			y: this.y,
		});
		if (!settlement) return;

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ totalClicks: settlement.totalClicks + numberOfClicks },
		);

		if (settlement.availableClicks === 0) {
			this.emitter.emit(EmitterEvents.PhaserWarningMessage, {
				x: this.x,
				y: this.y,
				text: "ui.taxes_collected",
			});

			return;
		}

		if (settlement.availableClicks < numberOfClicks) {
			numberOfClicks = settlement.availableClicks;
		}

		if (superClickerMultiplier > 1) {
			this.decreaseSuperClickerResources(superClickerMultiplier);
		}

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{
				availableClicks: Math.max(
					settlement.availableClicks - numberOfClicks,
					0,
				),
			},
		);

		this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
			x: this.x,
			y: this.y,
		});

		const clickReward = this.getClickReward(numberOfClicks);
		if (clickReward === undefined) return;
		const questStore = useQuestStore();
		const quest = questStore.getActiveQuests.find(
			(q: QuestActive): boolean => q.id === PIGGY_BANK_QUEST_ID,
		);
		if (
			(useShopStore().piggyBankOffers?.length ?? 0) > 0 &&
			quest &&
			quest.progress < PIGGY_BANK_MAX_PROGRESS &&
			Math.random() < 0.2
		) {
			QuestRepository.getInstance().manageQuestState(
				QuestObjectiveActions.PIGGY_BANK_PROGRESS,
				null,
				PIGGY_BANK_PROGRESS_PER_CLICK,
			);
			clickReward[ResourceType.PIGGY_BANK_PROGRESS] =
				PIGGY_BANK_PROGRESS_PER_CLICK;

			// Recalc offer data (*LB progress)
			OfferProvider.calculate([CalculationType.PIGGY_BANK]);
		}
		this.emitter.emit(EmitterEvents.PhaserTileClick, {
			x: this.x,
			y: this.y,
			reward: clickReward,
			randomX: true,
		});

		this.emitter.emit(EmitterEvents.PhaserSettlementAnimation, {
			x: this.x,
			y: this.y,
			value: false,
		});

		QuestRepository.getInstance().manageQuestState(
			QuestObjectiveActions.COLLECT_COINS,
			null,
			clickReward?.[ResourceType.COINS] || 0,
		);
		QuestRepository.getInstance().manageQuestState(
			QuestObjectiveActions.CLICK_ON_SETTLEMENT,
			null,
			superClickerMultiplier,
		);

		if (
			this.activeQuestObjective?.action ===
			UpgradeObjectiveAction.SETTLEMENT_ONCLICK_COINS_ACCUMULATED
		) {
			this.settlementsStore.setSettlementPropsByCoordinates(
				{ x: this.x, y: this.y },
				{
					questData: {
						current:
							(settlement.questData?.current ?? 0) +
							(clickReward?.[ResourceType.COINS] ?? 0),
					},
				},
			);
		}
	}

	public getClickReward(
		numberOfClicks = 1,
		simulated = false,
	): Partial<Record<ResourceType, number>> | undefined {
		if (!this.config.onClick) return;
		const { reward } = this.config.onClick();
		if (!reward) return;
		let clickReward = reward;

		// ak mame kupeny upgrade 29
		if (useUpgradesStore().getCurrentLevelById(UpgradeIds.fair_trade) > 0) {
			const mainSettlementConfig = getSettlementConfigByLevel(
				useSettlementsStore().mainLevel,
			);
			const { reward } = mainSettlementConfig.onClick();
			if (!reward) return;
			clickReward = reward;
		}
		if (useEventStore().attributes.type === EventCategory.FIREFEST) {
			const carboxTaxUpgradeValue =
				getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ff_carbon_tax,
				) +
				getUpgradeCurrentReplaceValueById(
					EventUpgradeIds.ff_special_02,
				);
			if (carboxTaxUpgradeValue > 0) {
				clickReward[ResourceType.EVENTIUM] =
					(clickReward[ResourceType.EVENTIUM] ?? 0) +
					carboxTaxUpgradeValue;
			}
		}

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null,
			) as Array<TileNeighbour>;

		const extraOnClickCoinsMultiplier = neighbours.reduce(
			(acc: number, neighbour: TileNeighbour): number => {
				const entity = this.tilerepo.createEntityByFrameName(
					neighbour.frameName,
				);
				if (!entity) return acc;

				const { production } = entity;
				if (
					production.has(
						ResourceType.PRODUCTION_ONCLICK_RESOURCE_MULTIPLIER,
					)
				) {
					return (
						acc +
						((production.get(
							ResourceType.PRODUCTION_ONCLICK_RESOURCE_MULTIPLIER,
						) as number) -
							1)
					);
				}

				return acc;
			},
			0,
		);

		for (const [key, value] of Object.entries(clickReward) as Array<
			[ResourceType, number]
		>) {
			let finalValue =
				key === ResourceType.COINS
					? value *
						(1 +
							getUpgradeCurrentReplaceValueById(
								UpgradeIds.friendly_visits,
							) +
							getUpgradeCurrentReplaceValueById(
								UpgradeIds.royal_earnings,
							) +
							extraOnClickCoinsMultiplier)
					: value;
			finalValue = ceilNumber(+finalValue.toFixed(2));

			clickReward[key] = finalValue * numberOfClicks;
			if (!simulated) {
				useResourcesStore().increase(
					key,
					clickReward[key],
					"settlement: click reward",
				);
			}
		}

		return clickReward;
	}

	public onReveal(): void {
		if (!this.isRevealed) return;

		// eslint-disable-next-line no-console
		console.log("[settlement castle] reveal", { x: this.x, y: this.y });

		// kontrola, ci uz bol settlement odhaleny v minulosti
		const settlement = this.settlementsStore.getSettlementByCoordinates({
			x: this.x,
			y: this.y,
		});
		if (!settlement) return;
		if (settlement.wasRevealed) return;

		SoundService.getInstance().playSettlementFound();

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{
				level:
					1 +
					getUpgradeCurrentReplaceValueById(UpgradeIds.settlers) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.kraken),
			},
		);
		this.level =
			1 +
			getUpgradeCurrentReplaceValueById(UpgradeIds.settlers) +
			getUpgradeCurrentReplaceValueById(UpgradeIds.kraken);

		const reward = {
			// TODO: Tuto to data pri 1. settlemente 1000 coinov, Davalo to povodne 2800, ak to takto chceme, tak activeSettlementCount ma byt este +_1.
			[ResourceType.COINS]: ceilNumber(
				this.tilerepo.activeSettlementCount ** 1.5 * 1_000,
			),
		};

		for (const [key, value] of Object.entries(reward) as Array<
			[ResourceType, number]
		>)
			useResourcesStore().increase(
				key,
				value,
				"settlement: reveal reward",
			);

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ wasRevealed: true },
		);

		const neighbourTileIndexesToBeExplored =
			this.tilerepo.getNeighbourTileIndexesByTileCoordinates(
				{
					x: this.x,
					y: this.y,
				},
				1,
				false,
				new Set<TileVisibilityType>([
					TileVisibilityType.BLACK,
					TileVisibilityType.FOG,
				]),
			);
		neighbourTileIndexesToBeExplored.forEach(
			(tileIndex: TileIndex): void => {
				this.tilerepo.setParam(
					tileIndex,
					TileEntityParam.VISIBILITY,
					TileVisibilityType.EXPLORED,
				);
				this.tilerepo.setParam(
					tileIndex,
					TileEntityParam.DURABILITY,
					DURABILITY_EXPLORED,
				);
				this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
					coordinates:
						this.tilerepo.getTileCoordinatesByTileIndex(tileIndex),
				});
				this.emitter.emit(EmitterEvents.TileRepositoryAddToRoadGraph, {
					tileIndex,
				});
			},
		);

		// empiricky zistene korekcie
		const getEuclideanDistanceCorrection = (distance: number): number => {
			if (distance >= 4) return -0.125;
			if (distance >= 3) return -0.25;
			if (distance >= 2) return -0.5;
			return 0;
		};

		const manhattanDistance =
			2 +
			getUpgradeCurrentReplaceValueById(UpgradeIds.cartography) +
			getUpgradeCurrentReplaceValueById(UpgradeIds.espionage);
		const neighbourTileIndexesToBeFogged = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{
					x: this.x,
					y: this.y,
				},
				manhattanDistance,
				false,
				new Set<TileVisibilityType>([TileVisibilityType.BLACK]),
			)
			.filter((tileIndex: TileIndex): boolean => {
				const { x, y } =
					this.tilerepo.getTileCoordinatesByTileIndex(tileIndex);
				const euclideanDistance = Phaser.Math.Distance.BetweenPoints(
					{ x: this.x, y: this.y },
					{ x, y },
				);
				return (
					euclideanDistance +
						getEuclideanDistanceCorrection(euclideanDistance) <=
					manhattanDistance
				);
			});

		neighbourTileIndexesToBeFogged.forEach((tileIndex: TileIndex): void => {
			this.tilerepo.setParam(
				tileIndex,
				TileEntityParam.VISIBILITY,
				TileVisibilityType.FOG,
			);
			this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
				coordinates:
					this.tilerepo.getTileCoordinatesByTileIndex(tileIndex),
				fade: true,
			});
		});

		this.emitter.emit(EmitterEvents.PhaserTileClick, {
			x: this.x,
			y: this.y,
			reward,
			messageTop: 260,
			messageNoAnim: true,
			messageScale: true,
		});

		this.emitter.emit(EmitterEvents.PhaserTutorialSettlementRevealed);

		this.emitter.emit(EmitterEvents.PhaserSuccessMessage, {
			x: this.x,
			y: this.y,
			text: "ui.town_found",
			messageNoAnim: true,
		});

		this.emitter.emit(EmitterEvents.PhaserSettlementAnimation, {
			x: this.x,
			y: this.y,
			value: true,
		});

		// Aby sa settlement vyfarbil na cerveno.
		this.tilerepo.refreshMainSettlementConnections();
	}

	public onActivate(): void {
		// if (!this.isRevealed) return;
		if (!this.isActive) return;

		// eslint-disable-next-line no-console
		console.log("[settlement castle] activate", { x: this.x, y: this.y });
		setTimeout(() => {
			useUserStore().setTownsConquered();
		}, 1000);

		// kontrola, ci uz bol settlement odhaleny v aktivovany
		const settlement = this.settlementsStore.getSettlementByCoordinates({
			x: this.x,
			y: this.y,
		});
		if (!settlement) return;
		if (settlement.wasActivated) return;

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ wasActivated: true },
		);

		this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
			x: this.x,
			y: this.y,
		});

		const sm = useSegmentManager();
		sm.markSegmentAsDirtyByCoordinates({ x: this.x, y: this.y });
		sm.run(true);

		// TODO: Tu netreba volat refresh?
		// Vola sa pri stavani cesty, mozno by bolo lepsie ho presunut sem, len potom sa bude volat dvojmo.
	}

	public onTick(multiplier = 1): void {
		if (!this.isActive) return;

		const settlement = this.settlementsStore.getSettlementByCoordinates({
			x: this.x,
			y: this.y,
		});
		if (!settlement) return;
		if (settlement.availableClicks >= this.maxClicks) return;

		const upgradedCoinsPerTicke =
			COINS_INCREMENT_PER_TICK *
			(1 + getUpgradeCurrentReplaceValueById(UpgradeIds.scribe));
		const availableClicks = Math.min(
			settlement.availableClicks + upgradedCoinsPerTicke * multiplier,
			this.maxClicks,
		);

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ availableClicks },
		);

		this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
			x: this.x,
			y: this.y,
		});
	}

	private decreaseSuperClickerResources(
		superClickerMultiplier: number,
	): void {
		switch (superClickerMultiplier) {
			case 0:
				break;
			case 2:
				useResourcesStore().decrease(
					ResourceType.CLICKER_2,
					1,
					"map: Clicker 2 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			case 3:
				useResourcesStore().decrease(
					ResourceType.CLICKER_3,
					1,
					"map: Clicker 3 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			case 5:
				useResourcesStore().decrease(
					ResourceType.CLICKER_5,
					1,
					"map: Clicker 5 used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_CLICK_MULTIPLIER,
				);
				break;
			default:
				useResourcesStore().decrease(
					ResourceType.SUPER_CLICKER,
					1,
					"map: super clicker used",
				);
				QuestRepository.getInstance().manageQuestState(
					QuestObjectiveActions.USE_SUPER_CLICKER,
				);
				break;
		}
	}

	public get isFull(): boolean {
		// Check if the 8 tiles around the settlement are full - there are buildings builded
		const neighbourTileIndexes =
			this.tilerepo.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1,
				false,
			);

		if (neighbourTileIndexes.length !== 8) return false;

		const fullTiles = neighbourTileIndexes.filter(
			(tileIndex: TileIndex): boolean => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				return !!tileId;
			},
		);

		return fullTiles.length === 8;
	}

	public fixIsRevealedStatus(): void {
		if (!this.isRevealed) return;
		if (
			this.settlementsStore.getSettlementByCoordinates({
				x: this.x,
				y: this.y,
			})?.wasRevealed
		)
			return;

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ wasRevealed: true },
		);
	}

	public fixIsActiveStatus(): void {
		if (!this.isActive) return;
		if (
			this.settlementsStore.getSettlementByCoordinates({
				x: this.x,
				y: this.y,
			})?.wasActivated
		)
			return;

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1,
				false,
			)
			.filter(
				(tileIndex: TileIndex): boolean =>
					this.tilerepo.getParam(
						tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED &&
					this.tilerepo.getParam(
						tileIndex,
						TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
					) === 1,
			);

		if (!neighbours.length) return;

		this.settlementsStore.setSettlementPropsByCoordinates(
			{ x: this.x, y: this.y },
			{ wasActivated: true },
		);

		this.emitter.emit(EmitterEvents.PhaserTileUpdateProgressBar, {
			x: this.x,
			y: this.y,
		});

		useSegmentManager().run(true);
	}
}

export default SettlementCastleBuildingTileEntity;
